import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

import Api from '../utils/api';
import { track } from '../utils/kanalytics';
import KoinAmount from '../components-v2/ui/KoinAmount';
import SpinningKoin from '../components-v2/koin/SpinningKoin';

const contentStorage = process.env.contentStorage;

function itemImage(item) {
    if (!item.image) return null;
    return item.itemType === 'card'
        ? `${contentStorage}/card-images/${item.image}`
        : `${contentStorage}/product-images/thumb_${item.image}.jpg`;
}

function formatPoints(value) {
    if (value == null) return null;
    return Number(value).toLocaleString('ro-RO');
}

const SORT_OPTIONS = [
    { value: 'price-asc', label: 'Pret crescator' },
    { value: 'price-desc', label: 'Pret descrescator' },
];

// Items missing a price always sink to the bottom, regardless of direction.
function sortItems(items, sort) {
    const dir = sort === 'price-desc' ? -1 : 1;
    return [...items].sort((a, b) => {
        const aMissing = a.pointsPrice == null;
        const bMissing = b.pointsPrice == null;
        if (aMissing || bMissing) return aMissing - bMissing;
        return (Number(a.pointsPrice) - Number(b.pointsPrice)) * dir;
    });
}

function SortSelect({ value, onChange }) {
    return (
        <label className="flex items-center gap-2 text-sm text-slate-400">
            <span className="font-medium">Sorteaza</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="rounded-full border border-solid border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-white/30 focus:border-amber-400/50 focus:outline-none"
            >
                {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} className="text-slate-900">
                        {opt.label}
                    </option>
                ))}
            </select>
        </label>
    );
}

function ItemCard({ item }) {
    const img = itemImage(item);
    const points = formatPoints(item.pointsPrice);
    const outOfStock = item.inStock === false;
    return (
        <div
            className={
                'group relative flex flex-col overflow-hidden rounded-2xl border border-solid border-white/10 bg-white/5 backdrop-blur transition-all duration-200 ' +
                (outOfStock
                    ? 'opacity-75'
                    : 'hover:-translate-y-1 hover:border-amber-400/50 hover:bg-white/[0.07] hover:shadow-[0_0_36px_-10px_rgba(234,179,8,0.45)]')
            }
        >
            <div className="relative aspect-square overflow-hidden bg-white">
                {img ? (
                    <img
                        src={img}
                        alt={item.name || ''}
                        loading="lazy"
                        className={
                            'h-full w-full object-contain p-3 transition-transform duration-300 ' +
                            (outOfStock ? 'grayscale' : 'group-hover:scale-105')
                        }
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs text-slate-600">
                        fara imagine
                    </div>
                )}
                {outOfStock && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-950/55">
                        <span className="rounded-full bg-slate-800/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-200 ring-1 ring-white/15">
                            Stoc epuizat
                        </span>
                    </div>
                )}
                {points != null && (
                    <div
                        className={
                            'absolute right-2.5 top-2.5 rounded-full px-3 py-1 text-xs font-extrabold shadow-lg ' +
                            (outOfStock
                                ? 'bg-slate-700 text-slate-300'
                                : 'bg-gradient-to-r from-amber-300 to-amber-500 text-slate-950 shadow-amber-500/20')
                        }
                    >
                        <KoinAmount value={points} />
                    </div>
                )}
            </div>
            <div className="flex flex-1 flex-col gap-2 p-4">
                <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-white">
                    {item.name || `${item.itemType} #${item.itemId}`}
                </h3>
                <span className="mt-auto inline-flex w-fit items-center rounded-md bg-white/5 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">
                    {item.itemType === 'card' ? 'Carte' : 'Produs'}
                </span>
            </div>
        </div>
    );
}

export default function KoinShop({ giveaway }) {
    const items = (giveaway && giveaway.items) || [];
    const name = (giveaway && giveaway.name) || '';
    const description = (giveaway && giveaway.description) || '';

    const [sort, setSort] = useState('price-asc');
    const sortedItems = useMemo(() => sortItems(items, sort), [items, sort]);

    // Counts page accesses, including direct hits / refreshes that the
    // _app.js route-change event misses. Distinct people dedup server-side.
    useEffect(() => {
        track('koin_shop_view', { giveaway: name || null, items: items.length });
    }, []);

    return (
        <>
            <Head>
                <title>{name ? `${name} — KOIN Shop` : 'KOIN Shop'}</title>
                <meta
                    name="description"
                    content="Transforma-ti KOINs in carti si produse din magazin."
                />
            </Head>

            <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#0b1c44] via-[#091433] to-[#05091c] text-white">
                {/* Ambient glow */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-40 left-1/2 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-amber-500/10 blur-[120px]"
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-40 right-0 h-[30rem] w-[30rem] rounded-full bg-blue-600/15 blur-[120px]"
                />

                {/* Top bar */}
                <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-5 py-5">
                    <Link href="/" aria-label="krit.ro">
                        <img
                            src="https://content.krit.ro/landing-assets%2Fcyberstorm-access%2Fkrit-logo.png"
                            alt="krit.ro"
                            className="h-14 w-auto sm:h-20"
                        />
                    </Link>
                    <Link
                        href="/"
                        className="rounded-full border border-solid border-white/15 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-white/30 hover:bg-white/5"
                    >
                        Inapoi in magazin
                    </Link>
                </header>

                {/* Hero */}
                <section className="relative z-10 mx-auto max-w-7xl px-5 pb-10 pt-8 text-center sm:pt-14">
                    {name && (
                        <h1 className="mx-auto max-w-3xl bg-gradient-to-b from-white to-slate-400 bg-clip-text text-4xl font-black leading-tight text-transparent sm:text-6xl">
                            {name}
                        </h1>
                    )}
                    {description && (
                        <p className="mx-auto mt-4 max-w-xl text-base text-slate-400 sm:text-lg">
                            {description}
                        </p>
                    )}
                    {items.length > 0 && (
                        <p className="mt-4 text-sm font-medium text-slate-500">
                            {items.length} disponibile
                        </p>
                    )}
                </section>

                {/* Grid */}
                <main className="relative z-10 mx-auto max-w-7xl px-5 pb-24">
                    {items.length === 0 ? (
                        <ComingSoon />
                    ) : (
                        <>
                            <div className="mb-6 flex justify-end">
                                <SortSelect value={sort} onChange={setSort} />
                            </div>
                            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                                {sortedItems.map((item) => (
                                    <ItemCard
                                        key={`${item.itemType}-${item.itemId}`}
                                        item={item}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </main>

                <p className="relative z-10 border-t border-solid border-white/10 py-8 text-center text-xs text-slate-500">
                    Preturile sunt exprimate in KOINs. Revendicarea se face in
                    magazin.
                </p>
            </div>
        </>
    );
}

function ComingSoon() {
    return (
        <div className="relative flex min-h-[45vh] flex-col items-center justify-center text-center">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-48 w-[85%] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/20 blur-[110px]"
            />

            <SpinningKoin className="mb-10" />

            <h2 className="animate-pulse bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-6xl font-black uppercase leading-none tracking-tight text-transparent drop-shadow-[0_0_25px_rgba(251,191,36,0.35)] sm:text-8xl lg:text-9xl">
                Coming Soon
            </h2>
            <p className="mt-6 max-w-md text-sm text-slate-400 sm:text-base">
                Adaugam articole in curand. Aduna KOINs pana atunci!
            </p>
        </div>
    );
}

KoinShop.getInitialProps = async (ctx) => {
    try {
        const res = await Api.get('/v2/giveaways/by-code/point-shop', null, null, ctx);
        return { giveaway: (res && res.data) || null };
    } catch (e) {
        return { giveaway: null };
    }
};
